import * as Sentry from "@sentry/react";
import LogRocket from "logrocket";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import { routePatternFromJsonRoutes } from "src/components/router/use-sentry-tracking";
import { SentryTags } from "src/components/router/router-types";
import { currentBuildSha } from "src/lib/chunk-reload";
import { monitoringEnvironment } from "src/lib/monitoring-env";

import { isReactQueryCancellation, isUserFacingError } from "./monitoring";

Sentry.init({
  beforeSend(event, hint) {
    if (isReactQueryCancellation(hint.originalException)) {
      return null;
    }

    if (isUserFacingError(hint.originalException)) {
      return null;
    }

    const logRocketSession = LogRocket.sessionURL;
    if (logRocketSession !== null && event.extra) {
      event.extra["LogRocket"] = logRocketSession;
    }

    // Best-effort normalize the transaction name from URL for error events.
    if (typeof window !== "undefined") {
      const { pathname } = window.location;
      const current = event.transaction || "";
      const looksGeneric =
        current === "" || current.startsWith("/:root_workspace_slug");

      if (looksGeneric) {
        // Prefer exact pattern from routes
        const fromRoutes = routePatternFromJsonRoutes(pathname);
        // Otherwise fallback to the current transaction name
        const base = fromRoutes ?? current;

        let name = base;

        // optionally append the page name if it exists in the tags
        const page = event.tags?.[SentryTags.PageName];
        if (page) {
          name = `${base} - ${page.toString()}`;
        }

        event.transaction = name;
      }
    }

    const error = hint.originalException;

    // Group network errors
    if (
      error &&
      typeof error === "object" &&
      "message" in error &&
      error.message &&
      typeof error.message === "string" &&
      (error.message.includes("NetworkError") ||
        error.message.includes("Failed to fetch"))
    ) {
      event.fingerprint = ["network-connection-error"];
    }
    return event;
  },
  enabled:
    import.meta.env.PROD &&
    import.meta.env.VITE_VERCEL_ENV !== "development" &&
    import.meta.env.VITE_VERCEL_ENV !== "preview",
  dsn: "https://b61c707a79854bc2902b1392410fa6ae@o1224454.ingest.sentry.io/6505512",
  // The build SHA baked into index.html — the same value Datadog RUM reports
  // as `version` — so Sentry issues can be correlated to deploys.
  release: currentBuildSha(),
  environment: monitoringEnvironment(import.meta.env.VITE_VERCEL_ENV),
  ignoreErrors: [
    /ResizeObserver loop/,
    /A network error occurred./,
    /signal is aborted without reason/,
  ],
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracesSampleRate: 0.5,
});
